import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { FadeUp } from "./FadeUp";
import { LANDING_IMAGES } from "../../../../constants/landing-images";

const CHECKLIST_ITEMS = [
  "No coding. You set up in chat.",
  "A public site with quotes and local search pages",
  "Job photos with GPS matching and tags",
  "Licenses and certs with reminders and sharing",
  "Quote requests in one inbox",
  "Hosting on your toolkit.build address",
];

type WhyToolKitSectionProps = {
  scrollToSection: (id: string) => void;
};

export const WhyToolKitSection: React.FC<WhyToolKitSectionProps> = ({
  scrollToSection,
}) => (
  <section id="why-toolkit" className="py-24 bg-card">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row items-center gap-16">
        <div className="lg:w-1/2 w-full relative">
          <FadeUp>
            <div className="relative rounded-2xl overflow-hidden aspect-square md:aspect-[4/5] shadow-2xl">
              <img
                src={LANDING_IMAGES.whyToolkit}
                alt="ToolKit construction software"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 md:bottom-12 md:-left-12 bg-card border border-border p-6 rounded-2xl shadow-xl max-w-[240px] z-20">
              <div className="flex items-center mb-2">
                <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center mr-3">
                  <CheckCircle2 className="w-6 h-6 text-success" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider">
                    Site live
                  </p>
                  <p className="text-2xl font-black text-foreground">1 min</p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="lg:w-1/2 w-full mt-12 lg:mt-0">
          <FadeUp delay={0.2}>
            <h3 className="text-premium-600 font-bold tracking-wider uppercase text-sm mb-3">
              Why ToolKit?
            </h3>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6 leading-tight font-display">
              $9 instead of an agency
            </h2>
            <p className="text-xl text-muted-foreground mb-10 leading-relaxed font-light">
              Chat for a minute. Get a live site, inbound quotes, job photos,
              and license reminders. Built for sole trades and small crews.
            </p>

            <div className="space-y-6 mb-12">
              {CHECKLIST_ITEMS.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start bg-muted/50 p-4 rounded-xl border border-border"
                >
                  <CheckCircle2 className="w-6 h-6 text-premium-600 mr-4 flex-shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium text-lg">
                    {item}
                  </span>
                </div>
              ))}
            </div>

            <Button
              type="button"
              variant="dark"
              onClick={() => scrollToSection("pricing")}
              className="group px-8 py-4 text-lg"
            >
              Start 30-day trial
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </FadeUp>
        </div>
      </div>
    </div>
  </section>
);
